"use client";

import {
  startTransition,
  useActionState,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { CheckCircle2, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

import { createInquiryAction, createResidentReservationAction, listResidentTimeAvailabilityAction, type InquiryActionSuccess } from "./actions";
import { eventBookingSpaces } from "@convex/lib/eventSpaceCatalog";
import { listSpaceAvailabilityAction } from "./availability-action";
import {
  analytics,
  getFormControlFieldKey,
  getFormControlValidityErrorType,
  getLeadAttribution,
  getPartySizeBucket,
} from "@/lib/analytics";
import {
  privateEventsConversionLabel,
  sendGoogleAdsConversion,
} from "@/lib/google-ads-conversions";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import {
  isPrivateEventTimeAllowed,
  PRIVATE_EVENT_TIME_OPTIONS,
} from "@/lib/private-events-time";
import { getPublicPageBrand } from "@/lib/public-brand-theme";
import { buildHomepageCopy } from "@/lib/marketing-location-copy";
import {
  isRomaBranch,
  validateRomaBranchField,
  type RomaBranch,
} from "@/lib/roma-branch";
import { RomaBranchChoice } from "@/components/roma/roma-branch-choice";
import {
  buildPrivateEventFaqItems,
  buildPrivateEventSpaceDetails,
  buildPrivateEventTypeDetails,
} from "./private-events-content";

const eventTypes = [
  {
    value: "birthday_anniversary",
    label: "Birthday & Anniversary",
    description:
      "Milestone dinners, family celebrations, and nights that need to feel warm.",
  },
  {
    value: "rehearsal_dinner",
    label: "Rehearsal Dinner",
    description: "Wedding-week gatherings with a neighborhood restaurant feel.",
  },
  {
    value: "wedding",
    label: "Wedding Reception",
    description:
      "Reception dinners and post-ceremony celebrations for your guests.",
  },
  {
    value: "corporate",
    label: "Corporate Event",
    description:
      "Team dinners, client meals, holiday parties, and business celebrations.",
  },
  {
    value: "holiday_party",
    label: "Holiday Party",
    description:
      "Seasonal dinners, end-of-year gatherings, and hosted group meals.",
  },
  {
    value: "bridal_baby_shower",
    label: "Bridal or Baby Shower",
    description:
      "Bridal and baby showers with menu direction and a practical setup.",
  },
  {
    value: "retirement",
    label: "Retirement Party",
    description:
      "Retirement dinners and celebrations for coworkers, family, and friends.",
  },
  {
    value: "fundraiser_gala",
    label: "Fundraiser / Gala",
    description:
      "Fundraisers, community dinners, and hosted gala-style gatherings.",
  },
  {
    value: "repast_memorial",
    label: "Memorial Gathering",
    description:
      "A thoughtful meal for family and friends when the room needs to be easy.",
  },
  {
    value: "private_party",
    label: "Private Party / Other",
    description:
      "Graduations, reunions, engagement parties, and other group occasions.",
  },
] as const;

const eventSpaces = [
  { value: "main_dining", label: "Dining Room" },
  { value: "full_buyout", label: "Full Buyout" },
  { value: "patio", label: "Outdoor Patio" },
  { value: "private_event_space", label: "Private Event Space" },
  { value: "not_sure", label: "Not Sure Yet" },
] as const;

const budgetPerPersonRanges = [
  "$30-$49",
  "$50-$74",
  "$75-$99",
  "$100+",
] as const;

const guestCountRanges = [
  { value: "9", label: "1-9" },
  { value: "19", label: "10-19" },
  { value: "29", label: "20-29" },
  { value: "39", label: "30-39" },
  { value: "49", label: "40-49" },
  { value: "50", label: "50+" },
] as const;

type PrivateEventType = (typeof eventTypes)[number]["value"];
type PrivateEventSpace = (typeof eventSpaces)[number]["value"];

const hostSnapshot = [
  {
    label: "Event types",
    detail:
      "Birthdays, showers, rehearsal dinners, corporate meals, holiday parties, memorial gatherings, and private parties.",
  },
  {
    label: "Available formats",
    detail:
      "Patio, main dining, full buyout, catering trays, or help choosing the right format.",
  },
] as const;

const romaHostSnapshot = [
  {
    label: "Private dining Las Vegas",
    detail:
      "Private dining in Las Vegas, Southern Highlands, and Henderson for birthdays, rehearsal dinners, corporate meals, anniversaries, showers, and group dining.",
  },
  {
    label: "Available formats",
    detail:
      "Enclosed private room for 12, or full restaurant buyout for up to 100, at Southern Highlands or Henderson.",
  },
] as const;

const privateEventGuideLinks = [
  {
    href: "/private-events/group-dining-catering-guide",
    label: "Group dining and catering guide",
    description:
      "Choose between reservations, private events, catering trays, and hosted meals.",
  },
  {
    href: "/private-events/corporate-events",
    label: "Corporate events",
    description: "Client dinners, team meals, and business gatherings.",
  },
  {
    href: "/private-events/rehearsal-dinners",
    label: "Rehearsal dinners",
    description: "Family-style meals and hosted pre-wedding dinners.",
  },
] as const;

const tuscanPlanningResourceLinks = [
  {
    href: "/catering",
    label: "Tuscan Cove catering and party platters",
    description:
      "Order custom American and Tuscan trays, pizza bundles, wings, sliders, and salad trays for off-site events.",
  },
  {
    href: "/menu",
    label: "Browse the full food and drink menu",
    description:
      "See starters, stone-fired pizzas, burgers, craft cocktails, wine, and beer to shape your event menu.",
  },
  {
    href: "/contact",
    label: "Contact the Tuscan Cove team",
    description:
      "Call (725) 223-0714 or send a message to check a date or talk through your private event.",
  },
] as const;

const romaPrivateEventGuideLinks = [
  {
    href: "/holiday-parties",
    label: "Holiday office parties",
    description:
      "Family-style holiday packages for Henderson and Southern Highlands offices.",
  },
  {
    href: "#private-events-request-form",
    label: "Start a private event request",
    description:
      "Share the occasion, date, guest count, and preferred format with the Roma team.",
  },
  {
    href: "/catering",
    label: "Plan catering trays",
    description:
      "Use the Roma catering form for pasta, chicken, antipasti, and family-style trays.",
  },
  {
    href: "/best/italian-catering-las-vegas",
    label: "Italian catering guide",
    description:
      "Compare off-site trays, pickup, delivery, and private-event catering paths.",
  },
  {
    href: "/best/italian-corporate-catering-las-vegas",
    label: "Corporate and office catering guide",
    description:
      "Plan corporate functions, client dinners, holiday parties, and office lunch trays for Las Vegas and Henderson teams.",
  },
  {
    href: "/best/private-dining-henderson-las-vegas",
    label: "Private dining planning guide",
    description:
      "Plan Henderson and Southern Highlands group dinners before sending the event request.",
  },
  {
    href: "/best/private-chef-southern-highlands-henderson",
    label: "Private chef at home guide",
    description:
      "Book an in-home chef dinner within 10 miles of the Southern Highlands or Henderson kitchens.",
  },
  {
    href: "/best/italian-restaurant-southern-highlands-las-vegas",
    label: "Southern Highlands location guide",
    description:
      "Use the Decatur Blvd guide for reservations, group dinners, and local details.",
  },
  {
    href: "/best/italian-restaurant-henderson-nv",
    label: "Henderson location guide",
    description:
      "Use the Horizon Ridge guide for late-night dining, groups, and private events.",
  },
  {
    href: "/events",
    label: "Events at Chef's Roma Kitchen",
    description:
      "Live music, holiday dinners, and seasonal nights at both kitchens.",
  },
] as const;

const tuscanPrivateEventPhotos = [
  {
    src: "/images/private-events/tuscan-cove-fire-table-patio-private-events.jpg",
    alt: "Outdoor fire-table patio set for private events",
  },
  {
    src: "/images/private-events/tuscan-cove-wine-garden-private-events.webp",
    alt: "Wine garden with a fire pit and outdoor group seating",
  },
  {
    src: "/images/private-events/tuscan-cove-poolside-patio-private-events.jpg",
    alt: "Poolside patio with fire tables and outdoor seating",
  },
  {
    src: "/images/private-events/tuscan-cove-pool-garden-private-events.webp",
    alt: "Pool garden with palm trees beside the outdoor patio",
  },
  {
    src: "https://static.spotapps.co/spots/bd/d716c5f44c47f8b264682c3a9c9f65/wide_medium",
    alt: "Large private event table set inside the restaurant",
  },
  {
    src: "https://static.spotapps.co/spots/9f/0c909a348a461aae0e7db49c8ef6b7/wide_medium",
    alt: "Restaurant bar seating area with TV screens",
  },
] as const;

const romaPrivateEventPhotos = [
  {
    src: "/images/roma-dark-funnel/hero-2.jpg",
    alt: "Chef's Roma Kitchen dining room set with white tablecloths for a private event",
  },
  {
    src: "/images/roma-dark-funnel/hero-3.jpg",
    alt: "Chef's Roma Kitchen dining room arranged for group dining",
  },
  {
    src: "/images/roma-dark-funnel/order-bg.jpg",
    alt: "Chef's Roma Kitchen private dining room prepared for guests",
  },
] as const;

type PrivateEventsFormState = { success: boolean; error?: string } | null;

const saffronInputClass =
  "h-[52px] w-full min-w-0 max-w-full rounded-[12px] border border-[#767676] bg-white px-4 text-[16px] leading-none text-[#1f1f1f] shadow-none outline-none transition-[border-color,box-shadow,transform] placeholder:text-[#666] focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60";

// iOS 26 WebKit overcounts horizontal padding when native date/time controls
// are width: 100%, making them spill beyond their form card.
const saffronDateTimeInputClass = `${saffronInputClass} appearance-none`;

const saffronTextareaClass =
  "min-h-[132px] w-full min-w-0 max-w-full resize-none rounded-[12px] border border-[#767676] bg-white px-4 py-3 text-[16px] leading-6 text-[#1f1f1f] shadow-none outline-none transition-[border-color,box-shadow] placeholder:text-[#666] focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 disabled:cursor-not-allowed disabled:opacity-60";

const fieldGroups: Record<string, string> = {
  customerName: "contact",
  customer_name: "contact",
  email: "contact",
  phone: "contact",
  eventType: "event",
  event_type: "event",
  eventDate: "event",
  event_date: "event",
  eventTime: "event",
  event_time: "event",
  guestCount: "party_size",
  guest_count: "party_size",
  budgetPerPerson: "budget",
  budget_per_person: "budget",
  eventSpace: "event_space",
  event_space: "event_space",
  residentUnitNumber: "resident",
  resident_unit_number: "resident",
  message: "details",
  romaBranch: "event",
  roma_branch: "event",
};

const fieldKeys: Record<string, string> = {
  customerName: "customer_name",
  eventType: "event_type",
  eventDate: "event_date",
  eventTime: "event_time",
  guestCount: "guest_count",
  budgetPerPerson: "budget_per_person",
  eventSpace: "event_space",
  residentUnitNumber: "resident_unit_number",
  romaBranch: "roma_branch",
};

const TAKEN_SPACE_DATE_ERROR = "That space is already booked on this date.";

function getLocalDateValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function addMonthsToDateValue(value: string, months: number) {
  const [year, month, day] = value.split("-").map(Number);
  return getLocalDateValue(new Date(year, month - 1 + months, day));
}

function isValidDateValue(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function isValidUsPhone(value: string) {
  const digits = value.replace(/\D/g, "");
  return (
    digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))
  );
}

function formatOutreachTime(timestamp: number) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(timestamp));
}

function isPrivateEventType(value: string): value is PrivateEventType {
  return eventTypes.some((type) => type.value === value);
}

function isPrivateEventSpace(value: string): value is PrivateEventSpace {
  return eventSpaces.some((space) => space.value === value);
}

type PrivateEventsPageVariant = "standard" | "resident";

export default function PrivateEventsPage({
  variant = "standard",
}: {
  variant?: PrivateEventsPageVariant;
}) {
  const isResidentVariant = variant === "resident";
  const formSource = isResidentVariant ? "resident_private_events_form" : "private_events_form";
  const leadSource = isResidentVariant ? "resident" : "private_events";
  const searchParams = useSearchParams();
  const {
    isDefaultLocation,
    location,
    restaurantCopy,
    siteConfig: activeSiteConfig,
  } = useMarketingLocationContext();
  const pageBrand = getPublicPageBrand(activeSiteConfig);
  const isTuscan = pageBrand === "tuscan-cove";
  const isRoma = pageBrand === "chefs-roma";
  const contentBrand = isTuscan ? "tuscan" : isRoma ? "roma" : "generic";
  // Roma has no outdoor space at either location (confirmed 2026-08-01), so the
  // patio must not be offerable as a bookable format on a Roma page.
  const locationSpaceKeys = new Set(eventBookingSpaces(location.id).map((space) => space.key));
  const availableEventSpaces = eventSpaces.filter((space) => space.value === "not_sure" || locationSpaceKeys.has(space.value));
  const romaLocationCards = isRoma
    ? (buildHomepageCopy(location).romaLocations ?? [])
    : [];
  // buildRestaurantCopy only special-cases Tuscan and Roma. Keep every other
  // brand on explicit, tenant-neutral copy sourced from the active site config.
  const privateEventsTitle =
    !isTuscan && restaurantCopy.privateEventsTitle.includes("Tuscan Cove")
      ? `Host your event at ${activeSiteConfig.name}`
      : restaurantCopy.privateEventsTitle;
  // Keep copy, links, and imagery explicit by tenant. Unknown/non-core brands
  // get their own configured venue image and neutral copy, never Tuscan/Roma.
  const genericGuideLinks = [
    {
      href: "#private-events-request-form",
      label: "Start a private event request",
      description: `Share the occasion, date, guest count, and preferred format with the ${activeSiteConfig.name} team.`,
    },
    {
      href: location.privatePartiesUrl || "#private-events-request-form",
      label: `View ${activeSiteConfig.name} private dining details`,
      description:
        "Review the venue's official event-space information and planning options.",
    },
  ];
  const guideLinks = isRoma
    ? romaPrivateEventGuideLinks
    : isTuscan
      ? privateEventGuideLinks
      : genericGuideLinks;
  const snapshotItems = isRoma
    ? romaHostSnapshot
    : isTuscan
      ? hostSnapshot
      : [
          {
            label: "Event types",
            detail:
              "Celebrations, rehearsal dinners, corporate meals, holiday parties, memorial gatherings, and private group dining.",
          },
          {
            label: "Location",
            detail: `${activeSiteConfig.address.line1}, ${activeSiteConfig.address.city}, ${activeSiteConfig.address.state}`,
          },
        ];
  const spacePhotos = isTuscan
    ? tuscanPrivateEventPhotos
    : isRoma
      ? romaPrivateEventPhotos
      : [
          {
            src: activeSiteConfig.heroImage || activeSiteConfig.ogImage,
            alt: `${activeSiteConfig.name} event space`,
          },
        ];
  const [submissionResult, setSubmissionResult] =
    useState<InquiryActionSuccess | null>(null);
  const [communicationsConsentError, setCommunicationsConsentError] = useState<
    string | null
  >(null);
  const [minEventDate, setMinEventDate] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventType, setEventType] = useState<PrivateEventType | "">("");
  const [eventSpace, setEventSpace] = useState<PrivateEventSpace | "">("");
  const [romaBranch, setRomaBranch] = useState<"" | RomaBranch>("");
  const [romaBranchError, setRomaBranchError] = useState<string | undefined>(
    undefined,
  );
  const availabilitySpace = isResidentVariant ? "private_event_space" : eventSpace;
  const [takenDates, setTakenDates] = useState<string[]>([]);
  const [residentAllDay, setResidentAllDay] = useState(true);
  const [residentTimeSummary, setResidentTimeSummary] = useState("All day");
  const [residentSlots, setResidentSlots] = useState<Array<{start: number; end: number; allDay: boolean}>>([]);
  const [residentAvailabilityMessage, setResidentAvailabilityMessage] = useState("");
  useEffect(() => {
    if (!isResidentVariant || !eventDate) return;
    let cancelled = false;
    setResidentSlots([]); setResidentAvailabilityMessage("Checking availability…");
    void listResidentTimeAvailabilityAction(eventDate).then(result => { if (!cancelled) { setResidentSlots(result.slots); setResidentAvailabilityMessage(result.error ?? (result.slots.length ? "Already reserved on this date:" : "No reservations on this date yet.")); } });
    return () => { cancelled = true; };
  }, [isResidentVariant, eventDate]);
  const [dateTakenError, setDateTakenError] = useState<string | null>(null);
  const hasStartedFormRef = useRef(false);
  const hasStartedInquiryRef = useRef(false);
  const lastAttemptedAtRef = useRef(0);
  const submissionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setMinEventDate(getLocalDateValue());
  }, []);

  useEffect(() => {
    setTakenDates([]);

    if (isResidentVariant || !availabilitySpace || !minEventDate) {
      return;
    }

    let cancelled = false;
    const fromDate = minEventDate;
    const toDate = addMonthsToDateValue(minEventDate, 12);

    void listSpaceAvailabilityAction({
      fromDate,
      toDate,
      spaceKey: availabilitySpace,
    }).then((cells) => {
      if (cancelled) return;
      const dates = [
        ...new Set(
          cells
            .filter((cell) => cell.spaceKey === availabilitySpace)
            .map((cell) => cell.date),
        ),
      ].sort();
      setTakenDates(dates);
    });

    return () => {
      cancelled = true;
    };
  }, [availabilitySpace, minEventDate, isResidentVariant]);

  useEffect(() => {
    if (!eventDate || !takenDates.includes(eventDate)) return;
    setEventDate("");
    setDateTakenError(TAKEN_SPACE_DATE_ERROR);
    toast.error(TAKEN_SPACE_DATE_ERROR);
  }, [eventDate, takenDates]);

  function markFormStarted(type = eventType || "unknown") {
    if (hasStartedFormRef.current) return;

    hasStartedFormRef.current = true;
    analytics.formStarted({
      category: "form",
      form_type: "private_events_inquiry",
      source: formSource,
      inquiry_type: type,
    });
  }

  function markInquiryStarted(type: string) {
    markFormStarted(type || "unknown");
    if (!type || hasStartedInquiryRef.current) return;

    hasStartedInquiryRef.current = true;
    analytics.inquiryStarted(type, formSource);
  }

  function getPrivateEventsFunnelProperties(
    properties: Record<string, string | number | boolean | undefined> = {},
  ) {
    return {
      form_type: "private_events_inquiry",
      source: formSource,
      lead_source: leadSource,
      inquiry_type: eventType || "unknown",
      ...properties,
    };
  }

  function trackPrivateEventsAttempted(
    type = eventType || "unknown",
    guestCount?: string | number,
  ) {
    const now = Date.now();
    if (now - lastAttemptedAtRef.current < 750) return;

    lastAttemptedAtRef.current = now;
    analytics.formAttempted(
      getPrivateEventsFunnelProperties({
        step: "details",
        inquiry_type: type || "unknown",
        party_size_bucket: guestCount
          ? getPartySizeBucket(guestCount)
          : undefined,
      }),
    );
  }

  function trackPrivateEventsValidationFailure(
    fieldKey: string,
    errorType: string,
    type = eventType || "unknown",
    guestCount?: string | number,
  ) {
    const normalizedFieldKey = fieldKeys[fieldKey] ?? fieldKey;

    analytics.formValidationFailed(
      getPrivateEventsFunnelProperties({
        step: "details",
        inquiry_type: type || "unknown",
        field_group:
          fieldGroups[fieldKey] ?? fieldGroups[normalizedFieldKey] ?? "details",
        field_key: normalizedFieldKey,
        error_type: errorType,
        party_size_bucket: guestCount
          ? getPartySizeBucket(guestCount)
          : undefined,
      }),
    );
  }

  const [formState, formAction, isPending] = useActionState(
    async (
      _prevState: PrivateEventsFormState,
      formData: FormData,
    ): Promise<PrivateEventsFormState> => {
      const customerName = String(formData.get("customerName") || "").trim();
      const email = String(formData.get("email") || "").trim();
      const phone = String(formData.get("phone") || "").trim();
      const eventDate = String(formData.get("eventDate") || "");
      const eventTime = String(formData.get("eventTime") || "");
      const guestCount = String(formData.get("guestCount") || "");
      const guestCountRange =
        guestCountRanges.find((range) => range.value === guestCount)?.label ||
        "";
      const budgetPerPerson = String(
        formData.get("budgetPerPerson") || "",
      ).trim();
      const message = String(formData.get("message") || "").trim();
      const residentUnitNumber = String(formData.get("residentUnitNumber") || "").trim();
      const residentInterestedInCatering = formData.get("residentInterestedInCatering") === "on";
      const residentAccessToBar = formData.get("residentAccessToBar") === "on";
      const smsConsent = formData.get("smsConsent") === "on";
      const submittedCommunicationsConsent =
        isTuscan && formData.get("communicationsConsent") === "on";
      const eventTypeFromForm = String(formData.get("eventType") || "");
      const eventSpaceFromForm = String(formData.get("eventSpace") || "");
      const submittedEventType = isPrivateEventType(eventTypeFromForm)
        ? eventTypeFromForm
        : eventType;
      const submittedEventSpace = isResidentVariant
        ? "private_event_space"
        : isPrivateEventSpace(eventSpaceFromForm)
          ? eventSpaceFromForm
          : eventSpace;
      const parsedGuestCount = Number.parseInt(guestCount, 10);
      markInquiryStarted(submittedEventType);
      trackPrivateEventsAttempted(submittedEventType || "unknown", guestCount);

      const romaBranchValidationError = validateRomaBranchField({
        isRoma,
        value: romaBranch,
      });
      setRomaBranchError(romaBranchValidationError);
      if (romaBranchValidationError) {
        trackPrivateEventsValidationFailure(
          "romaBranch",
          "missing_required",
          submittedEventType || "unknown",
          guestCount,
        );
        toast.error(romaBranchValidationError);
        return { success: false, error: romaBranchValidationError };
      }

      if (!submittedEventType) {
        trackPrivateEventsValidationFailure(
          "eventType",
          "missing_required",
          "unknown",
          guestCount,
        );
        toast.error("Please select an event type.");
        return { success: false, error: "Missing event type" };
      }

      if (!customerName || !email || !eventDate || !guestCount || (isResidentVariant && !residentUnitNumber)) {
        [
          ["customerName", customerName],
          ["email", email],
          ["eventDate", eventDate],
          ["guestCount", guestCount],
          ["residentUnitNumber", isResidentVariant ? residentUnitNumber : "not-required"],
        ]
          .filter(([, value]) => !value)
          .forEach(([fieldKey]) =>
            trackPrivateEventsValidationFailure(
              fieldKey,
              "missing_required",
              submittedEventType,
              guestCount,
            ),
          );
        toast.error("Please complete the required event details.");
        return { success: false, error: "Missing required fields" };
      }

      if (!isValidDateValue(eventDate) || eventDate < getLocalDateValue()) {
        trackPrivateEventsValidationFailure(
          "eventDate",
          "invalid_date",
          submittedEventType,
          guestCount,
        );
        toast.error("Please choose today or a future event date.");
        return { success: false, error: "Invalid event date" };
      }

      if (
        submittedEventSpace &&
        takenDates.includes(eventDate) && !submissionKeyRef.current
      ) {
        trackPrivateEventsValidationFailure(
          "eventDate",
          "space_taken",
          submittedEventType,
          guestCount,
        );
        setEventDate("");
        setDateTakenError(TAKEN_SPACE_DATE_ERROR);
        toast.error(TAKEN_SPACE_DATE_ERROR);
        return { success: false, error: TAKEN_SPACE_DATE_ERROR };
      }

      if (!isResidentVariant && !isPrivateEventTimeAllowed(eventTime)) {
        trackPrivateEventsValidationFailure(
          "eventTime",
          "outside_allowed_hours",
          submittedEventType,
          guestCount,
        );
        toast.error(
          "Please choose a time between 10 AM and 10 PM in 30-minute increments.",
        );
        return { success: false, error: "Event time outside allowed hours" };
      }

      if (!Number.isFinite(parsedGuestCount) || parsedGuestCount < 1) {
        trackPrivateEventsValidationFailure(
          "guestCount",
          "invalid_number",
          submittedEventType,
          guestCount,
        );
        toast.error("Please enter a valid guest count.");
        return { success: false, error: "Invalid guest count" };
      }

      if (submittedCommunicationsConsent && !isValidUsPhone(phone)) {
        const error = "Enter a valid phone number to agree to calls and texts.";
        setCommunicationsConsentError(error);
        trackPrivateEventsValidationFailure(
          "phone",
          "invalid_for_communications_consent",
          submittedEventType,
          guestCount,
        );
        toast.error(error);
        return { success: false, error };
      }

      setCommunicationsConsentError(null);

      const submittedMessage = [
        message || "Guest did not add extra notes.",
        isResidentVariant ? "Resident portal inquiry" : "",
        isResidentVariant ? `Resident unit: ${residentUnitNumber}` : "",
        isResidentVariant ? `Catering interest: ${residentInterestedInCatering ? "Yes" : "No"}` : "",
        isResidentVariant ? `Access to Bar: ${residentAccessToBar ? "Yes" : "No"}` : "",
        guestCountRange ? `Estimated guest range: ${guestCountRange}` : "",
        eventTime ? (isResidentVariant ? `Preferred start time: ${eventTime}` : "") : "Preferred time: Not sure yet",
        submittedEventSpace
          ? `Preferred format: ${submittedEventSpace}`
          : "Preferred format: Not sure yet",
      ]
        .filter(Boolean)
        .join("\n\n");

      let result: Awaited<ReturnType<typeof createInquiryAction>>;
      const submissionKey =
        submissionKeyRef.current ?? crypto.randomUUID();
      submissionKeyRef.current = submissionKey;

      try {
        result = isResidentVariant ? await createResidentReservationAction({
          phone: phone || undefined, eventTime: residentAllDay ? undefined : eventTime || undefined,
          reservationAllDay: residentAllDay, reservationEndTime: residentAllDay ? undefined : String(formData.get("reservationEndTime") || ""),
          submissionKey, customerName, email, residentUnitNumber, eventDate,
          eventType: submittedEventType, guestCount: parsedGuestCount, message: submittedMessage,
          attribution: getLeadAttribution(formSource),
        }) : await createInquiryAction({
          submissionKey,
          customerName,
          email,
          phone: phone || undefined,
          eventType: submittedEventType,
          eventDate,
          eventTime: eventTime || undefined,
          guestCount: parsedGuestCount,
          budgetPerPerson: isResidentVariant ? "not_sure" : budgetPerPerson || "not_sure",
          eventSpace: isResidentVariant ? "private_event_space" : submittedEventSpace || "not_sure",
          message: submittedMessage,
          smsConsent,
          communicationsConsent: submittedCommunicationsConsent,
          attribution: getLeadAttribution(formSource),
          romaBranch: isRoma && isRomaBranch(romaBranch) ? romaBranch : undefined,
        });
      } catch {
        // A dropped connection can reject before the server action returns its
        // normal error shape. Keep the form mounted and show the regular retry
        // path instead of replacing the page with the route error boundary.
        result = {
          success: false,
          error: "Unable to reach the server. Please try again.",
        };
      }

      if (result.success) {
        if (isResidentVariant) setResidentTimeSummary(residentAllDay ? "All day" : `${eventTime}–${String(formData.get("reservationEndTime") || "")} (local property time)`);
        const leadProperties = {
          lead_id: result.id,
          lead_type: "private_event",
          inquiry_type: submittedEventType,
          source: formSource,
          lead_source: leadSource,
          party_size_bucket: getPartySizeBucket(parsedGuestCount),
          communications_consent: submittedCommunicationsConsent,
          outreach_eligibility: result.outreachStatus,
        };
        analytics.formSubmitSucceeded(
          getPrivateEventsFunnelProperties({
            step: "server_action",
            ...leadProperties,
          }),
        );
        analytics.inquirySubmitted(
          submittedEventType,
          formSource,
          leadProperties,
        );
        analytics.leadCaptured(
          "private_event",
          formSource,
          leadProperties,
        );
        analytics.privateEventInquirySubmitted({
          ...leadProperties,
          inquiry_type: submittedEventType,
        });
        if (isDefaultLocation) {
          sendGoogleAdsConversion({
            conversionLabel: privateEventsConversionLabel,
            value: 100,
            currency: "USD",
            transactionId: result.id,
            userData: {
              email,
              phone,
            },
          });
        }
        toast.success(isResidentVariant ? "Your space is reserved." : "Inquiry submitted. We will be in touch soon.");
        submissionKeyRef.current = null;
        setSubmissionResult(result);
      } else {
        analytics.formSubmitFailed(
          getPrivateEventsFunnelProperties({
            step: "server_action",
            inquiry_type: submittedEventType,
            error_type: "server_rejected",
            party_size_bucket: getPartySizeBucket(parsedGuestCount),
          }),
        );
        if (result.error === TAKEN_SPACE_DATE_ERROR) {
          setEventDate("");
          setDateTakenError(TAKEN_SPACE_DATE_ERROR);
          toast.error(TAKEN_SPACE_DATE_ERROR);
        } else {
          toast.error(
            `We couldn't send your request. Please try again or call ${restaurantCopy.phoneDisplay}.`,
          );
        }
      }

      return result;
    },
    null,
  );

  const faqItems = buildPrivateEventFaqItems({
    shortName: restaurantCopy.shortName,
    phoneDisplay: restaurantCopy.phoneDisplay,
    brand: contentBrand,
  });
  const eventTypeDetails = buildPrivateEventTypeDetails({
    shortName: restaurantCopy.shortName,
    brand: contentBrand,
  });
  const spaceDetails = buildPrivateEventSpaceDetails({
    brand: contentBrand,
  });
  const paidSearchLanding =
    searchParams.get("utm_medium")?.toLowerCase() === "cpc" ||
    searchParams.has("gclid") ||
    searchParams.has("gbraid") ||
    searchParams.has("wbraid");

  if (submissionResult) {
    const receiptSmsQueued = ["queued", "pending"].includes(
      submissionResult.receiptSmsState,
    );
    const outreachScheduled =
      isTuscan &&
      ["queued", "scheduled"].includes(submissionResult.outreachStatus) &&
      typeof submissionResult.scheduledFor === "number";
    const outreachIsSoon =
      outreachScheduled &&
      submissionResult.scheduledFor! <= Date.now() + 4 * 60 * 1000;

    return (
      <main
        data-public-page-brand={pageBrand}
        data-roma-lead-page={isRoma ? "light" : undefined}
        className="roma-readable-lead-page min-h-[80dvh] bg-[#f7f5f2] px-4 py-14 text-[#1f1f1f]"
      >
        <section className="mx-auto max-w-[560px] rounded-[18px] border border-[#ddd7d1] bg-white px-6 py-10 text-center shadow-[0_24px_60px_-48px_rgba(45,35,28,0.55)]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-serif text-[26px] font-semibold leading-8">
            {isResidentVariant
              ? "Your space is reserved"
              : "Event inquiry received"}
          </h1>
          <p className="mt-3 text-[15px] leading-7 text-[#555]">
            {isResidentVariant ? <>The Tuscan Highlands private event space is reserved for {eventDate}. Time: {residentTimeSummary}. Check your email for the confirmation. Catering and bar service are arranged separately.</> : <>Thanks. We&apos;ll email a receipt while the {restaurantCopy.shortName} team reviews your date, guest count, and notes. This is an inquiry, not a confirmed booking. Our team will confirm availability and next steps with you.</>}
          </p>
          {isTuscan && receiptSmsQueued ? (
            <p className="mt-3 text-[15px] leading-7 text-[#555]">
              We also queued a text receipt to the phone number you provided.
            </p>
          ) : null}
          {outreachScheduled ? (
            <p className="mt-3 text-[15px] leading-7 text-[#555]">
              Marco, our AI scheduling assistant, may call{" "}
              {outreachIsSoon
                ? "in about two minutes"
                : `around ${formatOutreachTime(submissionResult.scheduledFor!)}`}
              .
            </p>
          ) : (
            <p className="mt-3 text-[15px] leading-7 text-[#555]">
              {isResidentVariant ? "For changes or cancellation, contact the Tuscan Highlands leasing office or property management." : "The team will follow up with the next practical step."}
            </p>
          )}
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              type="button"
              className="roma-lead-primary-action h-[52px] rounded-[12px] bg-[var(--roma-terracotta)] px-5 text-[15px] font-semibold text-[#0d0d0d] transition-colors hover:bg-[var(--roma-wine)] focus-visible:ring-[color:var(--roma-terracotta)]/25"
              onClick={() => {
                setSubmissionResult(null);
                setCommunicationsConsentError(null);
                setEventType("");
                setEventSpace("");
              }}
            >
              {isResidentVariant ? "Reserve another date" : "Send another inquiry"}
            </Button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      data-public-page-brand={pageBrand}
      data-roma-lead-page={isRoma ? "light" : undefined}
      className="roma-readable-lead-page min-h-[100dvh] w-full overflow-x-hidden bg-[#f7f5f2] text-[#1f1f1f]"
    >
      <section
        id="inquiry-form"
        className="mx-auto w-full max-w-[1120px] scroll-mt-site-header px-4 py-6 sm:px-6 sm:py-9 md:py-12 lg:px-8"
      >
        <div className="grid min-w-0 items-start gap-7 md:gap-10 lg:grid-cols-[minmax(0,0.86fr)_minmax(480px,1.14fr)] lg:grid-rows-[auto_1fr] lg:gap-12">
          <header className="min-w-0 lg:col-start-1 lg:row-start-1">
            <div className="min-w-0">
              <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-[var(--roma-terracotta)]">
                {isResidentVariant
                  ? "Tuscan Highlands resident portal"
                  : "Private events"}
              </p>
              <h1 className="mt-2 max-w-[620px] font-serif text-[clamp(2.125rem,8vw,2.625rem)] font-semibold leading-[1.08] tracking-[-0.02em] text-[#151515] lg:text-[48px]">
                {isResidentVariant
                  ? "Reserve the resident event space"
                  : privateEventsTitle}
              </h1>
              <p className="mt-4 max-w-[560px] text-[17px] leading-7 text-[#3f3f3f]">
                {isResidentVariant
                  ? "The community event space at Tuscan Highlands is just steps from your door, with optional catering and bar service from Tuscan Cove Bar & Patio."
                  : restaurantCopy.privateEventsIntro}
              </p>
              {isRoma ? (
                <div className="mt-5 grid max-w-[560px] gap-3 sm:grid-cols-2">
                  <div className="rounded-[14px] border border-[#ddd7d1] bg-white px-4 py-3">
                    <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
                      Private room
                    </p>
                    <p className="mt-1 text-[16px] font-semibold text-[#151515]">
                      Up to 12 guests
                    </p>
                    <p className="mt-1 text-[14px] leading-5 text-[#5b4a43]">
                      Enclosed room at Southern Highlands and Henderson.
                    </p>
                  </div>
                  <div className="rounded-[14px] border border-[#ddd7d1] bg-white px-4 py-3">
                    <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
                      Full buyout
                    </p>
                    <p className="mt-1 text-[16px] font-semibold text-[#151515]">
                      Up to 100 guests
                    </p>
                    <p className="mt-1 text-[14px] leading-5 text-[#5b4a43]">
                      Book the whole restaurant when the group is over 12.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </header>

          <div className="order-2 min-w-0 max-w-full lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:order-none">
            <section
              className="overflow-hidden rounded-t-[18px] border border-b-0 border-[#ddd7d1] bg-[#fdfbf9] px-4 py-5 sm:px-7"
              aria-labelledby="private-events-intake-heading"
            >
              <h2
                id="private-events-intake-heading"
                className="text-[21px] font-semibold leading-7 text-[#151515]"
              >
                {paidSearchLanding
                  ? "Check your private-event date first."
                  : "Tell us what you are planning."}
              </h2>
              <p className="mt-2 text-[14px] leading-6 text-[#5b4a43]">
                {isResidentVariant
                  ? "Choose all day or your start and end time. Catering and bar service are optional."
                  : "Share the occasion, date, group size, and best contact."}{" "}
                <span>Notes are optional.</span>
              </p>
            </section>

            <form
              id="private-events-request-form"
              className="min-w-0 max-w-full space-y-6 sm:space-y-7 overflow-hidden rounded-b-[18px] border border-[#ddd7d1] bg-white px-4 py-5 sm:py-7 shadow-[0_24px_60px_-48px_rgba(45,35,28,0.55)] sm:px-7 sm:py-7"
              onFocusCapture={() => markFormStarted()}
              onInvalidCapture={(event) => {
                const formData = new FormData(event.currentTarget);
                const formEventType = String(formData.get("eventType") || "");
                const submittedEventType = isPrivateEventType(formEventType)
                  ? formEventType
                  : eventType || "unknown";
                const guestCount = String(formData.get("guestCount") || "");
                const fieldKey = getFormControlFieldKey(event.target);

                trackPrivateEventsAttempted(submittedEventType, guestCount);
                trackPrivateEventsValidationFailure(
                  fieldKey,
                  getFormControlValidityErrorType(event.target),
                  submittedEventType,
                  guestCount,
                );
              }}
              onPointerDownCapture={() => markFormStarted()}
              onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);

                startTransition(() => {
                  formAction(formData);
                });
              }}
              onSubmitCapture={(event) => {
                const formData = new FormData(event.currentTarget);
                const formEventType = String(formData.get("eventType") || "");
                const submittedEventType = isPrivateEventType(formEventType)
                  ? formEventType
                  : eventType || "unknown";

                trackPrivateEventsAttempted(
                  submittedEventType,
                  String(formData.get("guestCount") || ""),
                );
              }}
            >
              {isRoma ? (
                <RomaBranchChoice
                  legend="Which Roma?"
                  intro="Pick the dining room for your event."
                  value={romaBranch}
                  onChange={setRomaBranch}
                  error={romaBranchError}
                  className="mb-6"
                />
              ) : null}

              {formState?.error ? (
                <div
                  role="alert"
                  aria-live="polite"
                  className="rounded-[12px] border border-red-200 bg-red-50 px-4 py-3 text-[14px] leading-6 text-red-800"
                >
                  We couldn&apos;t send your request. Please try again or call{" "}
                  <a
                    href={`tel:${restaurantCopy.phoneHref}`}
                    className="font-semibold underline underline-offset-2"
                  >
                    {restaurantCopy.phoneDisplay}
                  </a>
                  .
                </div>
              ) : null}

              <fieldset className="min-w-0">
                <legend className="text-[18px] font-semibold leading-7 text-[#151515]">
                  Tell us about your event
                </legend>
                <div className="mt-4 space-y-4 sm:space-y-5">
                  <SaffronField
                    label="Event type *"
                    htmlFor="eventType"
                    hint="Choose the closest match. The team can reroute it later."
                  >
                    <select
                      id="eventType"
                      name="eventType"
                      required
                      value={eventType}
                      onChange={(event) => {
                        const nextType = event.target.value as
                          | PrivateEventType
                          | "";
                        markInquiryStarted(nextType);
                        setEventType(nextType);
                      }}
                      disabled={isPending}
                      data-testid="private-events-event-type"
                      className={`${saffronInputClass} appearance-auto`}
                    >
                      <option value="" disabled>
                        Choose an event type
                      </option>
                      {eventTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </SaffronField>

                  <div className="grid min-w-0 grid-cols-1 gap-5 sm:grid-cols-2">
                    <SaffronField label="Date *" htmlFor="eventDate">
                      <Input
                        id="eventDate"
                        name="eventDate"
                        type="date"
                        min={minEventDate}
                        required
                        autoComplete="off"
                        data-testid="private-events-event-date"
                        data-taken-dates={takenDates.join(",")}
                        value={eventDate}
                        onChange={(event) => {
                          const nextDate = event.target.value;
                          if (takenDates.includes(nextDate)) {
                            setEventDate("");
                            setDateTakenError(TAKEN_SPACE_DATE_ERROR);
                            toast.error(TAKEN_SPACE_DATE_ERROR);
                            return;
                          }
                          setDateTakenError(null);
                          setEventDate(nextDate);
                        }}
                        disabled={isPending}
                        aria-invalid={dateTakenError ? true : undefined}
                        aria-describedby={
                          dateTakenError ? "eventDate-taken-error" : undefined
                        }
                        className={saffronDateTimeInputClass}
                      />
                      {dateTakenError ? (
                        <p
                          id="eventDate-taken-error"
                          role="alert"
                          className="mt-1 font-medium text-red-700"
                        >
                          {dateTakenError}
                        </p>
                      ) : null}
                    </SaffronField>

                    {!isResidentVariant && <SaffronField
                      label="Time"
                      htmlFor="eventTime"
                      hint="Optional. Choose a time."
                      hintPlacement="after"
                    >
                      <select
                        id="eventTime"
                        name="eventTime"
                        defaultValue=""
                        data-testid="private-events-event-time"
                        disabled={isPending}
                        className={`${saffronInputClass} appearance-auto`}
                      >
                        <option value="">Choose a time</option>
                        {PRIVATE_EVENT_TIME_OPTIONS.map((time) => (
                          <option key={time.value} value={time.value}>
                            {time.label}
                          </option>
                        ))}
                      </select>
                    </SaffronField>}
                  </div>
                  {isResidentVariant && <div className="space-y-4">
                    <SaffronField label="Reservation time" htmlFor="residentAllDay" hint="All times are local to Tuscan Highlands."><select id="residentAllDay" className={saffronInputClass} value={residentAllDay ? "all" : "timed"} onChange={e => setResidentAllDay(e.target.value === "all")} disabled={isPending}><option value="all">All day</option><option value="timed">Choose start and end time</option></select></SaffronField>
                    {!residentAllDay && <div className="grid grid-cols-2 gap-4"><SaffronField label="Start time *" htmlFor="eventTime"><input id="eventTime" name="eventTime" type="time" required step="60" className={saffronInputClass} disabled={isPending} /></SaffronField><SaffronField label="End time *" htmlFor="reservationEndTime" hint="Midnight means the end of this date."><input id="reservationEndTime" name="reservationEndTime" type="time" required step="60" className={saffronInputClass} disabled={isPending} /></SaffronField></div>}
                    {eventDate && <div role="status" className="text-sm text-[#555]"><p>{residentAvailabilityMessage}</p>{residentSlots.map((slot, i) => <p key={i}>{slot.allDay ? "All day" : `${String(Math.floor(slot.start / 60)).padStart(2,"0")}:${String(slot.start % 60).padStart(2,"0")}–${slot.end === 1440 ? "midnight" : `${String(Math.floor(slot.end / 60)).padStart(2,"0")}:${String(slot.end % 60).padStart(2,"0")}`}`}</p>)}</div>}
                  </div>}

                  <SaffronField
                    label="Number of people attending *"
                    htmlFor="guestCount"
                    hint="An estimate is fine."
                  >
                    <select
                      id="guestCount"
                      name="guestCount"
                      required
                      disabled={isPending}
                      defaultValue="9"
                      className={`${saffronInputClass} appearance-auto`}
                    >
                      {guestCountRanges.map((range) => (
                        <option key={range.value} value={range.value}>
                          {range.label}
                        </option>
                      ))}
                    </select>
                  </SaffronField>
                </div>
              </fieldset>

              <fieldset className="min-w-0 border-t border-[#e5dfda] pt-6 sm:pt-7">
                <legend className="text-[18px] font-semibold leading-7 text-[#151515]">
                  Where should we reply?
                </legend>
                <div className="mt-4 space-y-4 sm:space-y-5">
                  <SaffronField label="Name *" htmlFor="customerName">
                    <Input
                      id="customerName"
                      name="customerName"
                      required
                      autoComplete="name"
                      autoCapitalize="words"
                      enterKeyHint="next"
                      maxLength={100}
                      placeholder="Name"
                      disabled={isPending}
                      className={saffronInputClass}
                    />
                  </SaffronField>

                  {isResidentVariant ? (
                    <SaffronField
                      label="Unit number *"
                      htmlFor="residentUnitNumber"
                      hint="Enter your Tuscan Highlands unit number."
                    >
                      <Input
                        id="residentUnitNumber"
                        name="residentUnitNumber"
                        required={isResidentVariant}
                        autoComplete="off"
                        inputMode="numeric"
                        enterKeyHint="next"
                        maxLength={32}
                        placeholder="Example: 2040"
                        disabled={isPending}
                        className={saffronInputClass}
                      />
                    </SaffronField>
                  ) : null}

                  <div className="grid min-w-0 grid-cols-1 gap-5 sm:grid-cols-2">
                    <SaffronField label="Email *" htmlFor="email">
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        inputMode="email"
                        enterKeyHint="next"
                        maxLength={254}
                        spellCheck={false}
                        placeholder="Email"
                        disabled={isPending}
                        className={saffronInputClass}
                      />
                    </SaffronField>

                    <div className="min-w-0">
                      <SaffronField
                        label="Phone"
                        htmlFor="phone"
                        hint="Best for faster date checks."
                      >
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          inputMode="tel"
                          enterKeyHint="done"
                          maxLength={20}
                          placeholder="Phone"
                          disabled={isPending}
                          aria-invalid={
                            communicationsConsentError ? true : undefined
                          }
                          aria-describedby={
                            communicationsConsentError
                              ? "communicationsConsent-error"
                              : undefined
                          }
                          onChange={() => {
                            if (communicationsConsentError) {
                              setCommunicationsConsentError(null);
                            }
                          }}
                          className={saffronInputClass}
                        />
                      </SaffronField>

                      <p className="mt-3 text-sm text-[#565656]">We’ll reply by email.</p>
                    </div>
                  </div>
                </div>
              </fieldset>

              <fieldset className="min-w-0 border-t border-[#e5dfda] pt-6 sm:pt-7">
                <legend className="text-[18px] font-semibold leading-7 text-[#151515]">
                  Add any helpful details
                </legend>
                <div className="mt-4 space-y-4 sm:space-y-5">
                  {isResidentVariant ? (
                    <>
                      <SaffronField
                        label="Event space"
                        htmlFor="residentEventSpace"
                        hint="The one event space available to residents."
                      >
                        <select
                          id="residentEventSpace"
                          name="eventSpace"
                          defaultValue="private_event_space"
                          disabled={isPending}
                          className={`${saffronInputClass} appearance-auto`}
                        >
                          <option value="private_event_space">
                            Tuscan Highlands Private Event Space
                          </option>
                        </select>
                      </SaffronField>
                    </>
                  ) : (
                    <SaffronField
                      label="Preferred space or format"
                      htmlFor="eventSpace"
                      hint="Optional. Choose not sure yet if you want guidance."
                    >
                      <select
                        id="eventSpace"
                        name="eventSpace"
                        value={eventSpace}
                        onChange={(event) => {
                          setEventSpace(
                            event.target.value as PrivateEventSpace | "",
                          );
                          setDateTakenError(null);
                        }}
                        disabled={isPending}
                        className={`${saffronInputClass} appearance-auto`}
                      >
                        <option value="" disabled>
                          Choose a space or format
                        </option>
                        {availableEventSpaces.map((space) => (
                          <option key={space.value} value={space.value}>
                            {space.label}
                          </option>
                        ))}
                      </select>
                    </SaffronField>
                  )}

                  {!isResidentVariant ? (
                  <SaffronField
                    label="Budget per person"
                    htmlFor="budgetPerPerson"
                  >
                    <select
                      id="budgetPerPerson"
                      name="budgetPerPerson"
                      defaultValue=""
                      disabled={isPending}
                      className={`${saffronInputClass} appearance-auto`}
                    >
                      <option value="" disabled>
                        Choose a budget range
                      </option>
                      {budgetPerPersonRanges.map((range) => (
                        <option key={range} value={range}>
                          {range} per person
                        </option>
                      ))}
                      <option value="not_sure">Not Sure Yet</option>
                    </select>
                  </SaffronField>
                  ) : null}

                  {isResidentVariant ? (
                    <div className="grid min-w-0 gap-3 rounded-[14px] border border-[#e5dfda] bg-[#fdfbf9] p-4 text-[14px] leading-6 text-[#4b403a]">
                      <p className="font-semibold text-[#151515]">
                        Optional add-ons
                      </p>
                      <p className="text-[#4b403a]">
                        Tuscan Cove can handle the food and drinks — just
                        check a box.
                      </p>
                      <label className="flex items-start gap-3">
                        <Checkbox
                          id="residentInterestedInCatering"
                          name="residentInterestedInCatering"
                          disabled={isPending}
                          className="mt-[3px] border-[#8c8c8c]"
                        />
                        <span>Catering from Tuscan Cove</span>
                      </label>
                      <label className="flex items-start gap-3">
                        <Checkbox
                          id="residentAccessToBar"
                          name="residentAccessToBar"
                          disabled={isPending}
                          className="mt-[3px] border-[#8c8c8c]"
                        />
                        <span>Bar service during your event</span>
                      </label>
                    </div>
                  ) : null}

                  <SaffronField
                    label="Notes"
                    htmlFor="message"
                    hint={isResidentVariant ? "Optional." : "Optional. Add food, room, or timing details if you already know them."}
                  >
                    <Textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={2000}
                      placeholder={isResidentVariant ? "Anything else we should know?" : "Example: birthday dinner, patio if available."}
                      disabled={isPending}
                      className={saffronTextareaClass}
                    />
                  </SaffronField>
                </div>
              </fieldset>



              <Button
                type="submit"
                className="roma-lead-primary-action flex h-[52px] w-full items-center justify-center rounded-[12px] bg-[var(--roma-terracotta)] px-5 text-[15px] font-semibold text-[#0d0d0d] transition-colors hover:bg-[var(--roma-wine)] focus-visible:ring-[color:var(--roma-terracotta)]/25 disabled:cursor-not-allowed disabled:opacity-70"
                disabled={isPending}
                data-track="private_events_submit"
                data-track-location="private_events_form"
                data-testid="private-events-submit"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : isResidentVariant ? (
                  "Reserve space"
                ) : (
                  "Send event request"
                )}
              </Button>
            </form>
          </div>

          <div className="order-3 min-w-0 lg:col-start-1 lg:row-start-2">
            {!isResidentVariant ? (
              <figure className="relative aspect-[4/3] overflow-hidden rounded-[16px] bg-[#e9e6e1]">
                <Image
                  src={spacePhotos[0].src}
                  alt={spacePhotos[0].alt}
                  fill
                  sizes="(min-width: 1024px) 430px, calc(100vw - 32px)"
                  priority
                  quality={78}
                  className="object-cover"
                />
              </figure>
            ) : null}

            <section
              className="mt-7"
              aria-labelledby="private-events-snapshot-heading"
            >
              <h2
                id="private-events-snapshot-heading"
                className="font-serif text-[22px] font-semibold leading-7 text-[#151515]"
              >
                {isResidentVariant
                  ? "Resident events at Tuscan Highlands"
                  : isTuscan
                    ? "Private events in Southern Highlands"
                    : isRoma
                      ? "Private events in Southern Highlands and Henderson"
                      : `Private events at ${activeSiteConfig.name}`}
              </h2>
              <dl className="mt-4 space-y-4">
                {(isResidentVariant
                  ? [
                      {
                        label: "Who can book",
                        detail: "Tuscan Highlands residents.",
                      },
                      {
                        label: "Food and drink",
                        detail:
                          "Optional — Tuscan Cove catering or bar service.",
                      },
                    ]
                  : snapshotItems
                ).map((item) => (
                  <div
                    key={item.label}
                    className="border-l-2 border-[var(--roma-terracotta)] pl-4"
                  >
                    <dt className="text-[14px] font-semibold text-[#252525]">
                      {item.label}
                    </dt>
                    <dd className="mt-1 text-[14px] leading-6 text-[#555]">
                      {item.detail}
                    </dd>
                  </div>
                ))}
              </dl>
              {isResidentVariant ? (
                <p className="mt-6 border-t border-[#e5dfda] pt-4 text-[12px] leading-5 text-[#7a6f68]">
                  The private event room is owned and operated by Tuscan
                  Highlands Apartments. Tuscan Cove Bar &amp; Patio provides
                  catering and bar service only and does not manage the event
                  space. For questions or concerns regarding the room, please
                  contact the Tuscan Highlands leasing office or property
                  management.
                </p>
              ) : null}
            </section>
          </div>
        </div>
      </section>

      {!isResidentVariant && spacePhotos.length > 1 ? (
        <section className="mx-auto w-full max-w-[1120px] px-4 py-10 sm:px-6 md:py-14 lg:px-8">
          <div className="border-t border-[#dcd6d0] pt-9">
            <h2 className="font-serif text-[26px] font-semibold leading-8 text-[#151515]">
              See the space
            </h2>
            <div
              className={`mt-5 grid grid-cols-1 gap-4 min-[440px]:grid-cols-2 ${
                spacePhotos.length === 6
                  ? "md:grid-cols-5"
                  : spacePhotos.length > 3
                    ? "lg:grid-cols-4"
                    : "lg:max-w-[760px]"
              }`}
            >
              {spacePhotos.slice(1).map((photo) => (
                <figure
                  key={photo.src}
                  className="relative aspect-[4/3] overflow-hidden rounded-[16px] bg-[#e9e6e1]"
                >
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    fill
                    sizes={
                      spacePhotos.length > 3
                        ? "(min-width: 1024px) 260px, (min-width: 440px) 50vw, calc(100vw - 32px)"
                        : "(min-width: 1024px) 372px, (min-width: 440px) 50vw, calc(100vw - 32px)"
                    }
                    loading="lazy"
                    fetchPriority="low"
                    quality={70}
                    className="object-cover"
                  />
                </figure>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {!isResidentVariant ? (
      <>
      {isRoma && romaLocationCards.length > 0 ? (
        <section
          className="mx-auto w-full max-w-[1120px] px-4 py-10 sm:px-6 md:py-14 lg:px-8"
          aria-labelledby="private-events-location-cards-heading"
        >
          <div className="border-t border-[#dcd6d0] pt-9">
            <h2
              id="private-events-location-cards-heading"
              className="font-serif text-[26px] font-semibold leading-8 text-[#151515]"
            >
              Choose your location
            </h2>
            <p className="mt-3 max-w-[680px] text-[15px] leading-7 text-[#4f4f4f]">
              Recognize the right Roma before you arrive. Your event request can
              include either Southern Highlands or Henderson.
            </p>
            <div className="mt-6 grid gap-5 md:grid-cols-2">
              {romaLocationCards.map((locationCard) => (
                <article
                  key={locationCard.slug}
                  className="overflow-hidden rounded-[16px] border border-[#ddd7d1] bg-white"
                >
                  <a
                    href={locationCard.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative block aspect-[16/9] overflow-hidden bg-[#e9e6e1]"
                    aria-label={`Get directions to ${locationCard.shortName}`}
                  >
                    <Image
                      src={locationCard.imageSrc}
                      alt={locationCard.imageAlt}
                      fill
                      sizes="(min-width: 768px) 50vw, calc(100vw - 32px)"
                      loading="lazy"
                      fetchPriority="low"
                      quality={72}
                      className="object-cover"
                    />
                  </a>
                  <div className="p-5">
                    <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#666]">
                      {locationCard.slug === "henderson"
                        ? "Henderson · Horizon Ridge"
                        : "Southern Highlands · Las Vegas"}
                    </p>
                    <h3 className="mt-1 font-serif text-[22px] font-semibold leading-7 text-[#151515]">
                      {locationCard.shortName}
                    </h3>
                    <p className="mt-3 text-[14px] leading-6 text-[#4f4f4f]">
                      {locationCard.addressLine1}
                      <br />
                      {locationCard.addressLine2}
                    </p>
                    <a
                      href={locationCard.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex min-h-11 items-center font-semibold text-[var(--roma-terracotta)] underline underline-offset-4"
                    >
                      Get directions
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section
        className="mx-auto w-full max-w-[1120px] px-4 py-10 sm:px-6 md:py-14 lg:px-8"
        aria-labelledby="private-events-types-heading"
      >
        <div className="border-t border-[#dcd6d0] pt-9">
          <h2
            id="private-events-types-heading"
            className="font-serif text-[28px] font-semibold leading-9 text-[#151515]"
          >
            {isDefaultLocation
              ? "Private events at Tuscan Cove Bar & Patio in Southern Highlands"
              : "Private events we host"}
          </h2>
          <p className="mt-3 max-w-[720px] text-[15px] leading-7 text-[#4f4f4f]">
            {restaurantCopy.shortName} hosts birthdays, anniversaries, baby and
            bridal showers, rehearsal dinners, corporate and office functions,
            holiday parties, graduations, family reunions, retirement dinners,
            and memorial gatherings. Share your occasion in the request above
            and the team will help shape the right plan.
          </p>
          <div className="mt-7 grid gap-x-10 md:grid-cols-2">
            {eventTypeDetails.map((detail) => (
              <article
                key={detail.title}
                className="border-t border-[#dcd6d0] py-5"
              >
                <h3 className="text-[16px] font-semibold leading-6 text-[#1f1f1f]">
                  {detail.title}
                </h3>
                <p className="mt-2 text-[14px] leading-6 text-[#5d5d5d]">
                  {detail.body}
                </p>
              </article>
            ))}
          </div>
          {isDefaultLocation ? (
            <p className="mt-5 text-[14px] leading-6 text-[#4f4f4f]">
              Planning a business gathering? See the{" "}
              <Link
                href="/private-events/corporate-events"
                className="font-semibold text-[var(--roma-terracotta)] underline underline-offset-2"
              >
                corporate events guide
              </Link>
              . Hosting wedding week? Read about{" "}
              <Link
                href="/private-events/rehearsal-dinners"
                className="font-semibold text-[var(--roma-terracotta)] underline underline-offset-2"
              >
                rehearsal dinners at Tuscan Cove
              </Link>
              . Not sure which format fits your group? Start with the{" "}
              <Link
                href="/private-events/group-dining-catering-guide"
                className="font-semibold text-[var(--roma-terracotta)] underline underline-offset-2"
              >
                group dining and catering guide
              </Link>
              .
            </p>
          ) : null}
        </div>
      </section>

      <section
        className="mx-auto w-full max-w-[1120px] px-4 py-10 sm:px-6 md:py-14 lg:px-8"
        aria-labelledby="private-events-spaces-heading"
      >
        <div className="grid gap-8 border-t border-[#dcd6d0] pt-9 md:grid-cols-[0.8fr_1.2fr] md:gap-14">
          <div>
            <h2
              id="private-events-spaces-heading"
              className="font-serif text-[28px] font-semibold leading-9 text-[#151515]"
            >
              {isDefaultLocation
                ? "Indoor seating, a fire-pit patio, and catering to go"
                : "Spaces and formats for your group"}
            </h2>
            <p className="mt-3 text-[15px] leading-7 text-[#4f4f4f]">
              {restaurantCopy.shortName} sets your group with the table, menu,
              and service so you can host without the logistics. Tell the team
              your headcount and preferred format and they will recommend the
              best setup.
            </p>
            {isDefaultLocation ? (
              <p className="mt-5 text-[14px] leading-6 text-[#4f4f4f]">
                For trays and off-site events, plan with{" "}
                <Link
                  href="/catering"
                  className="font-semibold text-[var(--roma-terracotta)] underline underline-offset-2"
                >
                  Tuscan Cove catering
                </Link>
                , or browse the{" "}
                <Link
                  href="/menu"
                  className="font-semibold text-[var(--roma-terracotta)] underline underline-offset-2"
                >
                  full food and drink menu
                </Link>{" "}
                to shape your event menu.
              </p>
            ) : null}
          </div>
          <div>
            {spaceDetails.map((detail) => (
              <article
                key={detail.title}
                className="border-t border-[#dcd6d0] py-5 first:border-t-0 first:pt-0"
              >
                <h3 className="text-[16px] font-semibold leading-6 text-[#1f1f1f]">
                  {detail.title}
                </h3>
                <p className="mt-2 text-[14px] leading-6 text-[#5d5d5d]">
                  {detail.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {!isRoma ? (
        <section
          className="mx-auto w-full max-w-[1120px] px-4 py-10 sm:px-6 md:py-14 lg:px-8"
          aria-labelledby="private-events-location-heading"
        >
          <div className="border-t border-[#dcd6d0] pt-9">
            <h2
              id="private-events-location-heading"
              className="font-serif text-[26px] font-semibold leading-8 text-[#151515]"
            >
              {isTuscan
                ? "Tuscan Cove Bar & Patio in Southern Highlands, Las Vegas"
                : `Visit ${activeSiteConfig.name}`}
            </h2>
            <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            <div className="min-w-0 rounded-[16px] border border-[#ddd7d1] bg-white px-5 py-5">
              <dt className="text-[13px] font-semibold text-[#595959]">
                Address
              </dt>
              <dd className="mt-1 break-words text-[14px] leading-6 text-[#333]">
                {activeSiteConfig.address.line1},{" "}
                {activeSiteConfig.address.city},{" "}
                {activeSiteConfig.address.state} {activeSiteConfig.address.zip}
              </dd>
            </div>
            <div className="min-w-0 rounded-[16px] border border-[#ddd7d1] bg-white px-5 py-5">
              <dt className="text-[13px] font-semibold text-[#595959]">
                Phone
              </dt>
              <dd className="mt-1 text-[14px] leading-6 text-[#333]">
                <a
                  href={`tel:${restaurantCopy.phoneHref}`}
                  className="font-semibold text-[var(--roma-terracotta)] underline underline-offset-2"
                >
                  {restaurantCopy.phoneDisplay}
                </a>
              </dd>
            </div>
            {isDefaultLocation ? (
              <div className="min-w-0 rounded-[16px] border border-[#ddd7d1] bg-white px-5 py-5 sm:col-span-2 md:col-span-1">
                <dt className="text-[13px] font-semibold text-[#595959]">
                  Hours
                </dt>
                <dd className="mt-1 text-[14px] leading-6 text-[#333]">
                  Sun 10 AM to 8 PM, Mon to Sat 4 PM to 9 PM. Happy hour daily 4
                  to 6 PM.
                </dd>
              </div>
            ) : null}
            </dl>
          </div>
        </section>
      ) : null}

      <section className="mx-auto w-full max-w-[1120px] px-4 py-10 sm:px-6 md:py-14 lg:px-8">
        <div className="border-t border-[#dcd6d0] pt-9">
          <h2 className="font-serif text-[26px] font-semibold leading-8 text-[#151515]">
            Planning guides
          </h2>
          <div className="mt-5 grid gap-x-8 md:grid-cols-2">
            {guideLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="border-t border-[#dcd6d0] py-5 transition-colors hover:text-[var(--roma-terracotta)]"
              >
                <span className="block text-[15px] font-semibold leading-5 text-[#1f1f1f]">
                  {link.label}
                </span>
                <span className="mt-2 block text-[13px] leading-5 text-[#666]">
                  {link.description}
                </span>
              </Link>
            ))}
          </div>
          {isDefaultLocation ? (
            <div className="grid gap-x-8 md:grid-cols-2">
              {tuscanPlanningResourceLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="border-t border-[#dcd6d0] py-5 transition-colors hover:text-[var(--roma-terracotta)]"
                >
                  <span className="block text-[15px] font-semibold leading-5 text-[#1f1f1f]">
                    {link.label}
                  </span>
                  <span className="mt-2 block text-[13px] leading-5 text-[#666]">
                    {link.description}
                  </span>
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[860px] px-4 pb-16 pt-10 sm:px-6 md:pb-20 md:pt-14">
        <div className="border-t border-[#dcd6d0] pt-9">
          <h2 className="font-serif text-[28px] font-semibold leading-9 text-[#151515]">
            Common questions
          </h2>
          <div className="mt-5 border-y border-[#dcd6d0]">
            {faqItems.map((item) => (
              <details
                key={item.question}
                className="group border-b border-[#dcd6d0] last:border-b-0"
              >
                <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between gap-4 py-4 text-[16px] font-semibold leading-6 text-[#1f1f1f] marker:hidden">
                  <span>{item.question}</span>
                  <ChevronDown
                    aria-hidden="true"
                    className="h-5 w-5 shrink-0 text-[var(--roma-terracotta)] transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none"
                  />
                </summary>
                <div className="pb-5">
                  <p className="text-[14px] leading-6 text-[#5d5d5d]">
                    {item.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
      </>
      ) : null}
    </main>
  );
}

function SaffronField({
  label,
  htmlFor,
  hint,
  hintPlacement = "before",
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  hintPlacement?: "before" | "after";
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 space-y-2">
      <label
        htmlFor={htmlFor}
        className="block text-[15px] font-normal leading-6 text-[#232323]"
      >
        {label}
      </label>
      {hint && hintPlacement === "before" ? (
        <p className="text-[13px] leading-5 text-[#6b5c55]">{hint}</p>
      ) : null}
      {children}
      {hint && hintPlacement === "after" ? (
        <p className="text-[13px] leading-5 text-[#6b5c55]">{hint}</p>
      ) : null}
    </div>
  );
}
